import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { add, endOfDay, isAfter, isSameDay, parseISO, startOfDay } from 'date-fns';
import { Spacer } from 'design-system';
import { AppView } from 'design-system/AppView';
import BottomPanel from 'design-system/BottomPanel';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { Calendar } from 'design-system/calendar/Calendar';
import { Day } from 'design-system/calendar/Day';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteAll, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { editAutosaveUpcomingPayment, editSavingsPlan } from '../actions';
import { formatPauseAutosaveDate } from '../utils';
import { strings } from './PauseAutosaveInfoScreen';
const fetchOptions = {
    ...muteAll,
    throws: true,
    muteFetching: false,
};
const PauseAutosaveSelectDateScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const todayDate = useMemo(() => startOfDay(new Date()), []);
    const endOfToday = useMemo(() => endOfDay(new Date()), []);
    const [selected, setSelected] = useState(add(todayDate, { days: 1 }));
    const [, , fetching, fetch] = useFetchOld(fetchOptions);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const checkEnabled = useCallback((date) => {
        const currentDate = parseISO(date.dateString);
        return isAfter(currentDate, endOfToday);
    }, [endOfToday]);
    const onDayPress = useCallback((date) => {
        if (!checkEnabled(date)) {
            return;
        }
        setSelected(parseISO(date.dateString));
    }, [checkEnabled]);
    const { paddingHorizontalStyle, contentWidth } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const onSubmit = useCallback(async () => {
        try {
            await fetch(editSavingsPlan(route.params.autoSaveId, { isPaused: true, pausedUntil: selected.toISOString() }));
            if (route?.params?.paymentToSkip) {
                await fetch(editAutosaveUpcomingPayment(route.params.paymentToSkip.id, true));
            }
            navigation.navigateDeprecated('Success', {
                title: 'Success',
                subTitle: (<Text TextThin-16 Secondary>
              Your autosave is on hold until{' '}
              <Text Text-16 Secondary>
                {formatPauseAutosaveDate(selected)}
              </Text>
              . I’ll notify you when I start setting money aside again.
              {`\n\n${route?.params?.processingActivity ? strings.pendingPayment : strings.upcomingPayment}`}
            </Text>),
                onContinue: () => {
                    navigation.pop(3);
                    return true;
                },
                buttonText: 'Got it',
            });
        }
        catch {
            //
        }
    }, [route.params.autoSaveId, selected]);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView contentContainerStyle={paddingBottom} onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Select the date" subTitle="Pause autosave until👇"/>
          <Calendar style={[styles.calendar, { maxWidth: contentWidth }]} dayComponent={({ date }) => (<Day key={date?.dateString} date={date} onDayPress={onDayPress} isDisabled={date ? !checkEnabled(date) : false} isStartDay={date ? isSameDay(selected, parseISO(date.dateString)) : false}/>)}/>
        </Animated.ScrollView>
        <BottomPanel>
          <View style={styles.bottomPanelTextContainer}>
            <Text TextThin-16 Secondary centered>
              Your autosave will start{' '}
              <Text Text-16 Secondary>
                {formatPauseAutosaveDate(selected, true)}
              </Text>
            </Text>
          </View>
          <Spacer h={16}/>
          <Button brand onPress={onSubmit} title="Continue" isFetching={fetching}/>
        </BottomPanel>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    calendar: {
        width: '100%',
        alignSelf: 'center',
    },
    bottomPanelTextContainer: {
        paddingHorizontal: rem(24),
    },
});
export default PauseAutosaveSelectDateScreen;
